import { ArrowRight, Heart, MessageCircle } from "lucide-react"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { openExternalLink } from "@/shared/lib/open-external-link"
import { pluralize } from "@/shared/lib/pluralize"
import { PageTransition } from "@/shared/ui/page-transition"
import { SectionHeading } from "@/shared/ui/section-heading"

const REFERRAL_LINK = "https://paywall.pw/partners/vlvo2nqkedlo2n"
const SUPPORT_LINK = "https://t.me/kmkadmin"

const steps = [
	"Зарегистрируйтесь в партнёрской программе и получите свою ссылку",
	"Поделитесь ею с друзьями, партнёрами и в своих социальных сетях",
	"Получайте по 20% с каждого приведённого друга каждый месяц",
]

const rewards = [
	{ count: 5, emoji: "🥤", description: "Отправим вам ящик Кока-Колы" },
	{ count: 30, emoji: "🎥", description: "Разбор вашего канала от Коли в прямом эфире клуба" },
	{
		count: 50,
		emoji: "📋",
		description:
			"Полноценный разбор вашего канала, аналитика ниши и индивидуальная стратегия от ТОП-продюсера и технического директора студии. У нас в студии эта услуга стоит 200К",
	},
	{ count: 100, emoji: "🎬", description: "Коля ЛИЧНО продюсирует вашу съёмку" },
]

const CARD = "rounded-2xl bg-card p-4 ring-1 ring-white/10"
const PILL =
	"inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"

export function ReferralPage() {
	const { triggerHaptic } = useHaptic()

	const becomePartner = () => {
		triggerHaptic("medium")
		openExternalLink(REFERRAL_LINK)
	}

	return (
		<PageTransition>
			<header className="relative isolate -mt-[var(--app-body-padding-top)] pt-[var(--app-body-padding-top)]">
				<div
					aria-hidden
					className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(120%_70%_at_50%_0%,color-mix(in_oklab,var(--color-primary)_26%,transparent),transparent_70%)]"
				/>
				<div className="container mx-auto max-w-3xl px-3 pt-5 pb-7 sm:px-4">
					<p className="text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
						Партнёрам
					</p>
					<h1 className="mt-1 text-3xl leading-tight font-bold tracking-tight">Приводите своих</h1>
					<p className="mt-2 text-sm leading-snug text-muted-foreground">
						20% с каждой оплаты приведённого друга, каждый месяц, пока он в клубе.
					</p>
					<button
						type="button"
						onClick={becomePartner}
						className={`${PILL} mt-5 bg-primary text-primary-foreground shadow-lg shadow-primary/25`}
					>
						Стать партнёром
						<ArrowRight className="size-4" />
					</button>
				</div>
			</header>

			<div className="container mx-auto flex max-w-3xl flex-col gap-9 px-3 pb-6 sm:px-4 lg:pb-8">
				<section className={`${CARD} flex gap-3`}>
					<Heart className="mt-0.5 size-5 shrink-0 text-primary" />
					<p className="text-sm leading-relaxed">
						Если вы в клубе получили результат, порекомендуйте{" "}
						<span className="font-semibold">Контент могущество</span> тому, кто в вашем окружении
						давно думает о контенте. Это лучшая благодарность для меня.
					</p>
				</section>

				<section>
					<SectionHeading title="Как это работает" />
					<div className="relative">
						<span
							aria-hidden
							className="pointer-events-none absolute top-6 bottom-6 left-[15px] w-px bg-white/10"
						/>
						<ol className="space-y-3">
							{steps.map((step, index) => (
								<li key={step} className="relative pl-12">
									<span
										aria-hidden
										className="absolute top-3 left-0 grid size-8 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground ring-4 ring-background"
									>
										{index + 1}
									</span>
									<p className={`${CARD} text-sm leading-snug`}>{step}</p>
								</li>
							))}
						</ol>
					</div>
				</section>

				<section>
					<SectionHeading title="Система подарков" />
					<ul className="space-y-2.5">
						{rewards.map((reward) => (
							<li key={reward.count} className={`${CARD} flex gap-3`}>
								<span
									aria-hidden
									className="grid size-12 shrink-0 place-items-center rounded-xl bg-primary/15 text-2xl"
								>
									{reward.emoji}
								</span>
								<span className="min-w-0">
									<span className="block text-[11px] font-semibold tracking-[0.14em] text-primary uppercase">
										{pluralize(reward.count, ["человек", "человека", "человек"])}
									</span>
									<span className="mt-1 block text-sm leading-snug">{reward.description}</span>
								</span>
							</li>
						))}
					</ul>
				</section>

				<section className={CARD}>
					<div className="flex gap-3">
						<MessageCircle className="mt-0.5 size-5 shrink-0 text-primary" />
						<p className="text-sm leading-snug text-muted-foreground">
							Чтобы получить подарок, отправьте скрин из личного кабинета партнёра Paywall с
							количеством приведённых людей.
						</p>
					</div>
					<button
						type="button"
						onClick={() => {
							triggerHaptic("light")
							openExternalLink(SUPPORT_LINK)
						}}
						className={`${PILL} mt-4 w-full bg-white/[0.06] ring-1 ring-white/10`}
					>
						Написать в службу заботы
					</button>
				</section>

				<section className="rounded-3xl bg-gradient-to-br from-card via-card to-primary/15 p-5 ring-1 ring-white/10">
					<p className="text-lg leading-tight font-bold tracking-tight">Готовы начать?</p>
					<p className="mt-1.5 text-sm leading-snug text-muted-foreground">
						Ссылка придёт сразу после регистрации в партнёрской программе, это две минуты.
					</p>
					<button
						type="button"
						onClick={becomePartner}
						className={`${PILL} mt-4 w-full bg-primary text-primary-foreground shadow-lg shadow-primary/25`}
					>
						Стать партнёром
						<ArrowRight className="size-4" />
					</button>
				</section>
			</div>
		</PageTransition>
	)
}
